import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Typography, Divider, Grid } from '@material-ui/core';
import PageTitle from '../../components/ui/PageTitleUI';
import ButtonUI from '../../components/ui/ButtonUI';
import { useTranslation } from 'react-i18next';
import {
    Search as SearchIcon,
    BackspaceOutlined as BackspaceOutlinedIcon
} from '@material-ui/icons';
import { useAuth } from '../../hooks/auth';
import { useForm } from 'react-hook-form';
import DateField from '../../components/Form/Date';
import { useStyles } from '../../utils/DefaultStyles';
import SelectSupplier from '../../components/Form/SelectSupplier';
import ReportApi from '../../services/ReportApi';
import permissions from '../../utils/Permissions';
import Select from '../../components/Form/Select';
import * as Yup from 'yup'
import TextField from '../../components/Form/TextField';
import SelectStatus from '../Mvm/components/SelectStatus';
import CheckBox from "../../components/Form/Checkbox"

const MvmListReport: React.FC = () => {
    const classes = useStyles();
    const { user, hasPermission, isPortoReal } = useAuth();
    const { t: _t } = useTranslation("General");
    const { t } = useTranslation("Reports");
    const { t: tVal } = useTranslation("Validation");
    const history = useHistory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.REPORTS.MvmList))
            history.replace('/NoPermission');
    }, [history, user]);

    interface IFormPanel {
        submitDateStart: Date | null;
        submitDateEnd: Date | null;
        entranceDateStart: Date | null;
        entranceDateEnd: Date | null;
        status: string[];
        invoiceKey: string;
        suppliers: any | null;
        renderType: any;
        hasContainer: any;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, clearErrors, setError } = useForm<IFormPanel>({
        defaultValues: {
            submitDateStart: null,
            submitDateEnd: null,
            entranceDateStart: null,
            entranceDateEnd: null,
            status: [],
            invoiceKey: '',
            suppliers: [],
            hasContainer: null,
            renderType: { code: '2', label: 'Excel' }
        }
    });

    const formValidation = async (data: any) => {
        var result = false
        clearErrors();

        try {
            const schema = Yup.object().shape({
                submitDateStart: Yup.date().nullable().typeError(tVal("formatDate")),
                submitDateEnd: Yup.date().nullable().typeError(tVal("formatDate")),
                entranceDateStart: Yup.date().nullable().typeError(tVal("formatDate")),
                entranceDateEnd: Yup.date().nullable().typeError(tVal("formatDate")),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true
        }
        catch (err) {
            var errors: string[] = []

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    if (errors.filter(element => element.includes(error.message)).length === 0)
                        errors.push(error.message)

                    setError(error.path?.split('.')[0] as "entranceDateStart" | "entranceDateEnd" | "submitDateStart" | "submitDateEnd" | "invoiceKey" | "status" | "suppliers"
                        , { message: error.message });
                })
            }

            result = false;
        }

        return result
    }

    // Para submeter o form, precisa informar ao menos um intervalo de data: seja Emissao ou Entrada, para limitar o resultado do report
    const validateSubmitEntranceDates = (data: any) => {
        let validForm = true;
        const submitDates = [];
        const entranceDates = [];

        if (data.submitDateStart) submitDates.push(data.submitDateStart);
        if (data.submitDateEnd) submitDates.push(data.submitDateEnd);
        if (data.entranceDateStart) entranceDates.push(data.entranceDateStart);
        if (data.entranceDateEnd) entranceDates.push(data.entranceDateEnd);

        if (submitDates.length === 0 && entranceDates.length === 0) {
            setError("submitDateStart", { message: "Obrigatório informar um período de Emissão ou Entrada" });
            setError("entranceDateStart", { message: "Obrigatório informar um período de Emissão ou Entrada" });
            validForm = false;
        }
        if (submitDates.length === 1) {
            setError((data.submitDateStart === null ? "submitDateStart" : "submitDateEnd"), { message: "Obrigatório informar uma data de Início e Término de Emissão" });
            validForm = false;
        }
        if (entranceDates.length === 1) {
            setError((data.entranceDateStart === null ? "entranceDateStart" : "entranceDateEnd"), { message: "Obrigatório informar uma data de Início e Término de Entrada" });
            validForm = false;
        }

        return validForm;
    }

    const onSubmit = async (data: any) => {
        var validForm = await formValidation(data);
        validForm = validateSubmitEntranceDates(data) && validForm;
        if (!validForm)
            return;

        data.supplierNames = data.suppliers?.map((x: any) => (x.code + ' - ' + x.name));
        data.supplierIds = data.suppliers?.map((x: any) => (x.code));
        data.statuses = data.status.map((x: any) => (x.code));
        data.statusDescription = data.status.map((x: any) => (x.label));
        data.companyId = user.company.companyID;
        data.userName = user.name;
        data.renderType = data.renderType.code;

        ReportApi.printMvmList(data);
    };

    const cleanFilters = () => {
        reset();
    };

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={t("pageTitle") + t("mvmList.pageTitle")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form translate={undefined} onSubmit={handleSubmit(onSubmit)}  >
                        <Grid container spacing={1}>
                            <Grid item xs={6} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {t("titleParameters")}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI onClick={() => cleanFilters()} startIcon={<BackspaceOutlinedIcon />}>{_t("clean")}</ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />}>{_t("export")}</ButtonUI>
                            </Grid>
                        </Grid>
                        <Divider style={{ marginTop: '10px' }} />
                        <Grid container spacing={1}>
                            <Grid item xs={2}>
                                <SelectSupplier
                                    label={t("mvmList.suppliers")}
                                    name="suppliers"
                                    control={control}
                                    multiple={true}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={1}>
                                <DateField label={t("mvmList.submitDateStart")} name="submitDateStart" control={control} InputLabelProps={{ shrink: true }} fullWidth />
                            </Grid>
                            <Grid item xs={1}>
                                <DateField label={t("mvmList.submitDateEnd")} name="submitDateEnd" control={control} InputLabelProps={{ shrink: true }} fullWidth />
                            </Grid>

                            <Grid item xs={1}>
                                <DateField label={t("mvmList.entranceDateStart")} name="entranceDateStart" control={control} InputLabelProps={{ shrink: true }} fullWidth />
                            </Grid>

                            <Grid item xs={1}>
                                <DateField label={t("mvmList.entranceDateEnd")} name="entranceDateEnd" control={control} InputLabelProps={{ shrink: true }} fullWidth />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label={t('mvmList.invoiceKey')}
                                    name='invoiceKey'
                                    control={control}
                                    type="text"
                                    onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 44) }}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectStatus
                                    name="status"
                                    label={t('mvmList.statuses')}
                                    control={control}
                                    multiple
                                    fullWidth />
                            </Grid>
                            {isPortoReal() &&
                                <Grid item xs={2}>
                                    <CheckBox paddingtop={1}
                                        label={t('mvmList.indContainerNumber')}
                                        name="hasContainer"
                                        control={control}
                                    />
                                </Grid>
                            }
                            <Grid item xs={2}>
                                <Select
                                    label={_t("reportExportType")}
                                    name="renderType"
                                    control={control}
                                    options={[{ code: '2', label: 'Excel' }, { code: '1', label: 'PDF' }]}
                                    style={{ width: '100%' }} />
                            </Grid>
                            <Grid item xs={4}>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
        </Container>
    )
}
export default MvmListReport;
